import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
// material
import { Card, Table, Stack, TextField, TableRow, TableBody, TableCell, Container, Typography, TableContainer, Select, MenuItem, Grid, Button, Chip } from "@mui/material";
// components
import Page from "../../components/Page";
import Scrollbar from "../../components/Scrollbar";
import SearchNotFound from "../../components/SearchNotFound";
import ListHead from "../../components/common/ListHead";
import ListToolbar from "../../components/common/ListToolbar";
import MyPagination from "../../components/common/MyPagination";
import MyDateRangePicker from "../../components/common/my-date-range-picker/MyDateRangePicker";
import UserInfo from "../../components/common/UserInfo";
import axios from "axios";
import { alert } from "../../services/alert";
import { asyncRequest } from "../../services/network";

const TABLE_HEAD = [
  { id: "id", label: "ID", alignRight: false },
  { id: "uid", label: "用户", alignRight: false },
  { id: "orderSN", label: "订单号", alignRight: false },
  { id: "totalPrice", label: "订单原价", alignRight: false },
  { id: "discountAmount", label: "优惠金额", alignRight: false },
  { id: "payPrice", label: "实付金额", alignRight: false },
  { id: "payMethod", label: "支付方式", alignRight: false },
  { id: "status", label: "状态", alignRight: false },
  { id: "createdAt", label: "创建时间", alignRight: false },
];

const PAY_METHOD_MAP = {
  0: '权益金',
  1: '微信支付',
  2: '支付宝',
  3: '快捷支付',
  4: '消费券',
  5: '兑换券',
  6: '拉卡拉聚合支付',
  7: '汇付快捷支付',
  8: '汇付聚合支付',
  9: '银行卡快捷支付',
  10: '兑换券支付',
};

const STATUS_MAP = {
  0: { label: '已生成', color: 'info' },
  1: { label: '已使用', color: 'success' },
  2: { label: '已过期', color: 'default' },
};

export default function PaymentDiscountRecord() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);

  const [filter, setFilter] = useState({
    page: 0,
    size: 20,
    search: "",
    uid: "",
    orderSN: "",
    status: "",
    createdAt: null,
  });

  useEffect(() => {
    getItems();
  }, [filter]);

  async function getItems() {
    try {
      const { data, count } = await asyncRequest({
        path: "/finance/payment-discount/list",
        data: filter,
      });

      setItems(data || []);
      setTotal(count || 0);
    } catch (err) {
      console.error("获取优惠记录失败:", err);
      setItems([]);
      setTotal(0);
    }
  }

  async function getTotalStats() {
    try {
      const { data } = await asyncRequest({
        path: "/finance/payment-discount/stats",
        data: filter,
      });

      if (data) {
        setTotalDiscount(data.totalDiscount || 0);
      }
    } catch (err) {
      console.error("获取统计失败:", err);
    }
  }

  const handlePageChange = (event, value, newSize) => {
    if (newSize !== undefined) {
      setFilter({ ...filter, page: 0, size: newSize });
      return;
    }
    const newPage = value - 1;
    if (newPage === filter.page) return;
    setFilter({ ...filter, page: newPage });
  };

  function handleFilterChange(key, val) {
    setFilter({ ...filter, [key]: val });
  }

  const handleFilterByName = (event) => {
    setFilter({ ...filter, search: event.target.value });
  };

  function handleReset() {
    setFilter({
      page: 0,
      size: 20,
      search: "",
      uid: "",
      orderSN: "",
      status: "",
      createdAt: null,
    });
  }

  async function handleExport() {
    const { data, msg } = await asyncRequest({
      path: "/finance/payment-discount/export",
      data: filter,
    });

    alert(msg, "success");
  }

  return (
    <Page title="支付优惠记录">
      <Container maxWidth="2000px">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            支付优惠记录
          </Typography>
          <Button variant="outlined" onClick={handleExport}>
            导出数据
          </Button>
        </Stack>

        {/* 筛选条件 */}
        <Card sx={{ mb: 3, p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="用户ID"
                placeholder="请输入用户ID"
                value={filter.uid}
                onChange={(e) => handleFilterChange("uid", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="订单号"
                placeholder="请输入订单号"
                value={filter.orderSN}
                onChange={(e) => handleFilterChange("orderSN", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Select
                fullWidth
                size="small"
                value={filter.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                displayEmpty
              >
                <MenuItem value="">全部状态</MenuItem>
                <MenuItem value="0">已生成</MenuItem>
                <MenuItem value="1">已使用</MenuItem>
                <MenuItem value="2">已过期</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MyDateRangePicker
                value={filter.createdAt}
                onChange={(val) => handleFilterChange("createdAt", val)}
                placeholder="选择时间范围"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <Button fullWidth variant="contained" onClick={() => getItems()}>
                查询
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <Button fullWidth variant="outlined" onClick={handleReset}>
                重置
              </Button>
            </Grid>
          </Grid>
        </Card>

        {/* 统计卡片 */}
        {totalDiscount > 0 && (
          <Card sx={{ mb: 3, p: 2, bgcolor: 'info.lighter' }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Typography variant="subtitle2" color="info.dark">
                当前筛选条件下：
              </Typography>
              <Chip
                label={`优惠总额：¥${parseFloat(totalDiscount).toFixed(2)}`}
                color="info"
                variant="filled"
              />
            </Stack>
          </Card>
        )}

        {/* 数据表格 */}
        <Card>
          <ListToolbar filterName={filter.search} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead headLabel={TABLE_HEAD} />
                <TableBody>
                  {items.map((row) => {
                    const { id, uid, orderSN, totalPrice, discountAmount, payPrice, payMethod, status, createdAt, user } = row;

                    return (
                      <TableRow hover key={id} tabIndex={-1}>
                        <TableCell align="left">{id}</TableCell>
                        <TableCell align="left">
                          <UserInfo
                            user={{
                              uid: user?.uid || uid,
                              nickname: user?.nickname,
                              mobile: user?.mobile,
                              avatar: user?.avatar,
                            }}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'primary.main',
                              cursor: 'pointer',
                              fontFamily: 'monospace',
                            }}
                            onClick={() => navigate(`/order/detail?orderSN=${orderSN}`)}
                          >
                            {orderSN}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2">¥{parseFloat(totalPrice).toFixed(2)}</Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                            -¥{parseFloat(discountAmount).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            ¥{parseFloat(payPrice).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2">
                            {PAY_METHOD_MAP[payMethod] || `方式${payMethod}`}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Chip
                            label={STATUS_MAP[status]?.label || '未知'}
                            color={STATUS_MAP[status]?.color || 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="left">
                          {moment(createdAt).format("YYYY-MM-DD HH:mm:ss")}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                {items.length === 0 && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filter.search} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <MyPagination total={total} page={filter.page} size={filter.size} onChange={handlePageChange} />
        </Card>

        {/* 说明卡片 */}
        <Card sx={{ mt: 3, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            📋 说明
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2">
              • 每笔享受支付优惠的订单都会在此记录
            </Typography>
            <Typography variant="body2">
              • 优惠金额由平台补贴，不影响商户结算
            </Typography>
            <Typography variant="body2">
              • 优惠算法：0.01元 ~ min(订单金额×1%, 5元) 随机优惠
            </Typography>
            <Typography variant="body2">
              • 状态说明：已生成（订单创建）、已使用（订单支付完成）、已过期（订单取消/超时）
            </Typography>
          </Stack>
        </Card>
      </Container>
    </Page>
  );
}
